/**
 * Integra la app con history.pushState / popstate para que el botón "atrás"
 * del sistema navegue dentro de la PWA antes de salir.
 */

import {
  parseMlvHashPerfilVendedorUid,
  parseMlvHashProductId,
  parseMlvHashServiciosRoute,
} from './mlvDeepLink.js';

const KEY = 'mlv';

function hashFor(state) {
  if (!state || state.t === 'catalog') return '#/';
  if (state.t === 'product' && state.id)
    return `#/p/${encodeURIComponent(state.id)}`;
  if (state.t === 'perfilVendedor' && state.uid) {
    const u = String(state.uid).trim();
    return `#/perfil-vendedor/${encodeURIComponent(u)}`;
  }
  if (state.t === 'profile') return '#/profile';
  if (state.t === 'admin') return '#/admin';
  if (state.t === 'login') return '#/login';
  if (state.t === 'premiumPlan') return '#/premium';
  if (state.t === 'feed' && state.startId) {
    const c = encodeURIComponent(state.categoriaId || 'general');
    const s = encodeURIComponent(state.startId);
    return `#/feed/${c}/${s}`;
  }
  if (state.t === 'vendedor') {
    const mode = state.mode || 'datos';
    return `#/vendedor/${encodeURIComponent(mode)}`;
  }
  if (state.t === 'servicios') {
    if (state.mode === 'miPerfil') return '#/servicios/mi-perfil';
    if (state.mode === 'ficha' && state.uid) {
      return `#/servicios/pro/${encodeURIComponent(state.uid)}`;
    }
    return '#/servicios';
  }
  return '#/';
}

/**
 * @param {object} options
 * @param {(state: object) => void | Promise<void>} options.applyState - Sincronizar UI con el estado (t: 'catalog'|'product'|...)
 * @param {() => boolean} [options.isExitConfirmEnabled] - Si false, no se muestra confirmación al salir (p.ej. pantallas especiales)
 * @param {string} [options.exitMessage]
 */
export function createMlvBrowserHistory({
  applyState,
  isExitConfirmEnabled = () => true,
  exitMessage = '¿Querés salir?',
}) {
  let historyReady = false;
  /** Evita reentrar en popstate mientras aplicamos estado */
  let applying = false;

  function finishApply() {
    queueMicrotask(() => {
      applying = false;
    });
  }

  function ensureHistoryBaseline() {
    if (historyReady) return;
    historyReady = true;
    if (!history.state?.[KEY]) {
      const pid = parseMlvHashProductId();
      if (pid) {
        history.replaceState(
          { [KEY]: { t: 'product', id: pid } },
          '',
          hashFor({ t: 'product', id: pid })
        );
        return;
      }
      const pvu = parseMlvHashPerfilVendedorUid();
      if (pvu) {
        history.replaceState(
          { [KEY]: { t: 'perfilVendedor', uid: pvu } },
          '',
          hashFor({ t: 'perfilVendedor', uid: pvu })
        );
        return;
      }
      const srv = parseMlvHashServiciosRoute();
      if (srv) {
        history.replaceState({ [KEY]: srv }, '', hashFor(srv));
        return;
      }
      history.replaceState({ [KEY]: { t: 'catalog' } }, '', hashFor({ t: 'catalog' }));
    }
  }

  /** Si el hash no coincide con `history.state` (p. ej. state viejo perfil-ventas y URL perfil-vendedor), alinear. */
  function reconcileStateWithHash() {
    if (typeof window === 'undefined') return;
    ensureHistoryBaseline();
    const cur = window.location.hash || '';
    const wrapped = window.history.state?.[KEY];
    if (cur && cur === hashFor(wrapped)) return;
    const pid = parseMlvHashProductId();
    if (pid) {
      window.history.replaceState(
        { [KEY]: { t: 'product', id: pid } },
        '',
        hashFor({ t: 'product', id: pid })
      );
      return;
    }
    const pvu = parseMlvHashPerfilVendedorUid();
    if (pvu) {
      window.history.replaceState(
        { [KEY]: { t: 'perfilVendedor', uid: pvu } },
        '',
        hashFor({ t: 'perfilVendedor', uid: pvu })
      );
      return;
    }
    const srv = parseMlvHashServiciosRoute();
    if (srv) {
      window.history.replaceState({ [KEY]: srv }, '', hashFor(srv));
    }
  }

  /**
   * Llamar antes de mostrar una pantalla que debe generar entrada en el historial.
   */
  function push(state) {
    ensureHistoryBaseline();
    history.pushState({ [KEY]: state }, '', hashFor(state));
  }

  /**
   * Reemplaza la entrada actual (p.ej. tras login o al fijar catálogo como raíz).
   */
  function replace(state) {
    ensureHistoryBaseline();
    history.replaceState({ [KEY]: state }, '', hashFor(state));
  }

  function handlePopState(ev) {
    if (applying) return;
    const wrapped = ev.state?.[KEY];
    if (wrapped) {
      applying = true;
      Promise.resolve(applyState(wrapped)).catch(console.error).finally(finishApply);
      return;
    }

    if (!isExitConfirmEnabled()) {
      return;
    }

    const leave = window.confirm(exitMessage);
    if (leave) {
      if (window.matchMedia?.('(display-mode: standalone)')?.matches) {
        window.close();
      }
      history.back();
      return;
    }

    applying = true;
    history.pushState({ [KEY]: { t: 'catalog' } }, '', hashFor({ t: 'catalog' }));
    Promise.resolve(applyState({ t: 'catalog' })).catch(console.error).finally(finishApply);
  }

  window.addEventListener('popstate', handlePopState);

  return {
    push,
    replace,
    ensureHistoryBaseline,
    reconcileStateWithHash,
    /** Quitar listener (tests) */
    dispose() {
      window.removeEventListener('popstate', handlePopState);
    },
  };
}
